import { PermissionGate } from "@components/PermissionGate";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPencil, IconPlus, IconRefresh, IconTrash } from "@tabler/icons-react";

import { WithAppointmentsNavbar } from "@layouts/WithAppointmentsNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";
import { TruncatedCell } from "@components/Table/TruncatedCell";

import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { DEFAULT_PAGINATION } from "@constants/index";

import { useDeleteSlotTemplate } from "@hooks/mutations/slotTemplate/useDeleteSlotTemplate";
import { useGenerateSlots } from "@hooks/mutations/slotTemplate/useGenerateSlots";
import { useGetSlotTemplates } from "@hooks/queries/slotTemplate/useGetSlotTemplates";
import { useTableColumnCount } from "@hooks/useTableColumnCount";
import { useAuth } from "@contexts/auth";

import { SlotTemplate } from "@customTypes/slotTemplate";

import { AddSlotTemplateModal } from "./components/AddSlotTemplateModal";
import { EditSlotTemplateModal } from "./components/EditSlotTemplateModal";
import { StyledSlotTemplates } from "./SlotTemplates.styled";


export const SlotTemplates = () => {
  const { t, i18n } = useTranslation();
  const { id, roles } = useAuth();
  const isDoctorView = roles.includes("doctor") && !roles.includes("admin");

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<SlotTemplate | null>(null);
  const [currentTemplateId, setCurrentTemplateId] = useState<string>("");
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);

  const { columnCount, tableRef } = useTableColumnCount();

  const {
    data: templatesData,
    isLoading: isTemplatesLoading,
    isError: isTemplatesError,
    isSuccess: isTemplatesSuccess,
  } = useGetSlotTemplates({
    doctorAssigned: isDoctorView ? id : undefined,
    pagination: {
      page: currentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
  });

  const {
    mutate: deleteTemplate,
    isLoading: isDeleteLoading,
  } = useDeleteSlotTemplate();

  const {
    mutate: generateSlots,
    isLoading: isGenerateLoading,
  } = useGenerateSlots();

  const handleEdit = (template: SlotTemplate) => {
    setSelectedTemplate(template);
    openEditModal();
  };

  const handleDelete = (templateId: string) => {
    if (confirm(t("confirm-delete-template"))) {
      deleteTemplate({ templateId, deleteFutureSlots: true });
    }
  };

  const handleGenerate = (templateId: string) => {
    generateSlots({ templateId });
  };

  const getRecurrenceDisplay = (template: SlotTemplate) => {
    const { type, daysOfWeek } = template.recurrence;

    if (type === "daily") return t("daily");

    if (type === "weekly" || type === "specific_days") {
      if (!daysOfWeek || daysOfWeek.length === 0) return t("no-days-selected");

      if (daysOfWeek.length === 7) return t("daily");

      // Check for weekdays (Mon-Fri)
      if (daysOfWeek.length === 5 &&
          daysOfWeek.includes(1) && daysOfWeek.includes(2) &&
          daysOfWeek.includes(3) && daysOfWeek.includes(4) &&
          daysOfWeek.includes(5)) {
        return t("weekdays");
      }

      // Map day numbers to short names
      const dayNames = [
        t("sun-short"),
        t("mon-short"),
        t("tue-short"),
        t("wed-short"),
        t("thu-short"),
        t("fri-short"),
        t("sat-short"),
      ];

      return daysOfWeek
        .sort((a, b) => a - b)
        .map(day => dayNames[day])
        .join(", ");
    }

    return type;
  };

  const getTimeRangeDisplay = (template: SlotTemplate) => {
    const days = Object.keys(template.dailySchedule).map(Number).sort((a, b) => a - b);
    if (days.length === 0) return t("no-schedule-configured");

    // Get total number of time blocks
    const totalBlocks = days.reduce((sum, day) => {
      return sum + (template.dailySchedule[day]?.length || 0);
    }, 0);

    // Get first block's details for display
    const firstDay = days[0];
    const firstBlock = template.dailySchedule[firstDay]?.[0];

    if (!firstBlock) return t("no-schedule-configured");

    return (
      <>
        <Text fz={14}>
          {firstBlock.startTime} - {firstBlock.endTime}
        </Text>
        <Text fz={12} c="dimmed">
          {totalBlocks > 1
            ? `${firstBlock.slotDurationMinutes}min slots (+${totalBlocks - 1} more blocks)`
            : `${firstBlock.slotDurationMinutes}min slots`
          }
        </Text>
      </>
    );
  };

  const rows = useMemo(() => {
    return templatesData?.newData?.map((template) => (
      <Table.Tr key={template.id}>
        <Table.Td>
          <Stack gap={0}>
            <TruncatedCell value={template.name} charCount={30} />
            {template.description && (
              <Text fz={12} c="dimmed" lineClamp={1}>
                {template.description}
              </Text>
            )}
          </Stack>
        </Table.Td>
        <Table.Td>
          <TruncatedCell value={template.doctorName || "N/A"} charCount={20} />
        </Table.Td>
        <Table.Td>
          {getTimeRangeDisplay(template)}
        </Table.Td>
        <Table.Td>
          <Text fz={14}>{getRecurrenceDisplay(template)}</Text>
        </Table.Td>
        <Table.Td>
          <Badge color={template.isActive ? "green" : "gray"} size="sm">
            {template.isActive ? t("active") : t("inactive")}
          </Badge>
        </Table.Td>
        <Table.Td w={"min-content"}>
          <Group gap={3} justify="end">
            <ActionIcon
              variant="outline"
              color="blue"
              onClick={() => {
                setCurrentTemplateId(template.id);
                handleGenerate(template.id);
              }}
              loading={isGenerateLoading && currentTemplateId === template.id}>
              <IconRefresh size={16} stroke={1.5} />
            </ActionIcon>
            <PermissionGate permission="appointments.slots"><ActionIcon
              variant="outline"
              onClick={() => handleEdit(template)}>
              <IconPencil size={16} stroke={1.5} />
            </ActionIcon></PermissionGate>
            <PermissionGate permission="appointments.slots"><ActionIcon
              variant="outline"
              color="red"
              onClick={() => handleDelete(template.id)}
              loading={isDeleteLoading}>
              <IconTrash size={16} stroke={1.5} />
            </ActionIcon></PermissionGate>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [templatesData, i18n.language, isDeleteLoading, isGenerateLoading, currentTemplateId]);

  return (
    <WithSidebar hasBackground hasDashboardNavbar>
      <WithAppointmentsNavbar>
        <StyledSlotTemplates>
          <div className="header">
            <Group w={"100%"} justify="space-between" align="center">
              <h1>{t("slot-templates")}</h1>
              <PermissionGate permission="appointments.slots"><Button
                leftSection={<IconPlus size={16} />}
                onClick={openAddModal}>
                {t("create-template")}
              </Button></PermissionGate>
            </Group>
          </div>

          <Stack gap={"xs"} w={{ base: "100%" }}>
            <Table
              ref={tableRef}
              withRowBorders={true}
              withTableBorder
              highlightOnHover
              highlightOnHoverColor="var(--clr-gray-100)">
              <Table.Thead>
                <Table.Tr bg={"var(--clr-gray-100)"}>
                  <Table.Th w={300}>{t("name")}</Table.Th>
                  <Table.Th w={200}>{t("doctor")}</Table.Th>
                  <Table.Th w={200}>{t("time-range")}</Table.Th>
                  <Table.Th w={150}>{t("recurrence")}</Table.Th>
                  <Table.Th w={100}>{t("status")}</Table.Th>
                  <Table.Th w={120} ta={"right"}>
                    {t("action")}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {renderLoadingErrorData({
                  isLoading: isTemplatesLoading,
                  isError: isTemplatesError,
                  isSuccess: isTemplatesSuccess,
                  dataLength: templatesData?.newData?.length ?? 0,
                  errorComponent: (
                    <TableErrorRow
                      colSpan={columnCount}
                      message={t("encountered-error")}
                    />
                  ),
                  emptyDataComponent: (
                    <TableEmptyRow
                      colSpan={columnCount}
                      message={t("no-templates-found")}
                    />
                  ),
                  loadingComponent: (
                    <TableSkeletonRows
                      columns={columnCount}
                      skeletonProps={{
                        widths: ["lg", "md", "md", "md", "sm", "sm"],
                        rightAlignLastColumn: true,
                      }}
                    />
                  ),
                  dataComponent: rows,
                })}
              </Table.Tbody>
            </Table>
            <TablePagination
              paginationData={templatesData?.pagination || {}}
              paginatedRows={templatesData?.newData?.length || 0}
              onPageChange={setCurrentPage}
              isLoading={isTemplatesLoading}
            />
          </Stack>

          <AddSlotTemplateModal opened={addModalOpened} close={closeAddModal} />
          <EditSlotTemplateModal
            opened={editModalOpened}
            close={closeEditModal}
            template={selectedTemplate}
          />
        </StyledSlotTemplates>
      </WithAppointmentsNavbar>
    </WithSidebar>
  );
};
